import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiThemeProvider, Typography } from '@material-ui/core';
import classNames from 'classnames';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import {
  axuminTheme,
  poslumaTheme,
  borderDarkGrey,
  darkGrey,
  purple,
  yellow,
  poslumaBlue,
  poslumaDarkBlue,
  transparentYellowBox,
  universalStyles,
} from '../../../app/theme';
import FINDINGS from '../../../constants/findings.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  axuminContainer: {
    width: '100%',
    marginTop: 60,
    borderTop: `4px solid ${purple}`,
    paddingTop: 65,
    display: 'flex',
    flexDirection: 'column',
  },
  poslumaContainer: {
    width: '100%',
    marginTop: 60,
    borderTop: `4px solid ${poslumaDarkBlue}`,
    paddingTop: 65,
    display: 'flex',
    flexDirection: 'column',
  },
  headerRow: {
    width: 612,
    overflowX: 'visible',
    borderBottom: `1px solid ${borderDarkGrey}`,
    paddingBottom: 21,
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    width: 78,
    height: 'auto',
    marginRight: 27,
  },
  headerTextBox: {
    display: 'flex',
    flexDirection: 'column',
  },
  axuminTitle: {
    display: 'inline',
    width: '50vw',
    minWidth: 612,
    margin: 0,
  },
  poslumaTitle: {
    display: 'inline',
    width: '50vw',
    minWidth: 612,
    margin: 0,
  },
  axuminSubtitle: {
    fontWeight: 'normal',
    fontStyle: 'italic',
  },
  poslumaSubtitle: {
    fontWeight: 'normal',
    fontStyle: 'italic',
    color: poslumaDarkBlue,
  },
  axuminFindingsButton: {
    color: purple,
    fontWeight: 'bold',
    fontSize: 18,
    margin: '25px 0 39px',
    cursor: 'pointer',
  },
  poslumaFindingsButton: {
    color: poslumaDarkBlue,
    fontWeight: 'bold',
    fontSize: 22,
    margin: '25px 0 39px',
    cursor: 'pointer',
  },
  buttonText: {
    textDecoration: 'underline',
  },
  poslumaButtonText: {
    textDecoration: 'underline',
    fontSize: 22,
  },
  text: {
    '& > p': {
      color: darkGrey,
      fontSize: 18,
      lineHeight: 1.5,
    },
  },
  poslumaText: {
    '& > p': {
      fontSize: 22,
      color: poslumaDarkBlue,
      lineHeight: 1.5,
    },
  },
  yellowBox: {
    margin: '30px 25px 0 -25px',
    width: '100%',
    padding: '21px 25px',
    backgroundColor: transparentYellowBox,
  },
  rowWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  row: {
    width: '100%',
    marginBottom: 30,
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginRight: 61,
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  columnTitle: {
    color: darkGrey,
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  axuminFindingsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: purple,
  },
  poslumaFindingsTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    paddingTop: 25,
    color: poslumaDarkBlue,
  },
  axuminFindingsList: {
    listStyle: 'none',
    margin: 0,
    fontSize: 18,
    color: darkGrey,
    lineHeight: 1.3,
    padding: '0 0 0 36px',
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2022"',
      color: yellow,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
  poslumaFindingsList: {
    listStyle: 'none',
    margin: 0,
    fontSize: 18,
    color: darkGrey,
    lineHeight: 1.3,
    padding: '0 0 0 36px',
    paddingBottom: 30,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2022"',
      color: poslumaBlue,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
}));

type FindingsPanelProps = {
  trainingId: number;
  module: string;
  page: number;
  // setNextDisabledFindings: (disabled: boolean) => void;
};

const FindingsPanel = ({ trainingId, module, page }: FindingsPanelProps): JSX.Element => {
  const classes = useStyles();
  const theme = trainingId === 1 ? axuminTheme : poslumaTheme;
  const [expanded, setExpanded] = useState(false);
  const findingsContents = FINDINGS[module][page.toString()];
  const caseStudyPath = IMAGE_PATHS.CASE_STUDY[trainingId.toString() as keyof typeof IMAGE_PATHS.CASE_STUDY];

  const togglePanel = () => {
    if (!expanded) {
      // setNextDisabledFindings(false);
    }
    setExpanded(!expanded);
  };

  return (
    <MuiThemeProvider theme={theme}>
      <div className={trainingId === 1 ? classes.axuminContainer : classes.poslumaContainer}>
        <div className={classes.headerRow}>
          <img
            className={classes.icon}
            src={`${BASE_URL}${caseStudyPath.CASE_STUDY_TAB_1_IMAGE.path}`}
            alt={caseStudyPath.CASE_STUDY_TAB_1_IMAGE.alt}
          />
          <div className={classes.headerTextBox}>
            <Typography variant="h5" className={trainingId === 1 ? classes.axuminTitle : classes.poslumaTitle}>
              Please review and evaluate the images below
            </Typography>
            <Typography
              variant="h5"
              className={
                trainingId === 1
                  ? classNames(classes.axuminTitle, classes.axuminSubtitle)
                  : (classes.poslumaTitle, classes.poslumaSubtitle)
              }
            >
              To advance, review the findings by selecting &quot;+ Findings&quot; below
            </Typography>
          </div>
        </div>
        <div>
          <ReactMarkdown rehypePlugins={[rehypeRaw]} className={trainingId === 1 ? classes.text : classes.poslumaText}>
            {findingsContents.review.textQuestion}
          </ReactMarkdown>
          <div
            className={classNames(
              classes.yellowBox,
              findingsContents.layoutInColumns ? classes.columnWrapper : classes.rowWrapper,
            )}
          >
            {findingsContents.review.reviewImages.map((image, index) => (
              <div className={findingsContents.layoutInColumns ? classes.column : classes.row} key={image.path}>
                {findingsContents.review.reviewCaptions && (
                  <Typography className={classes.columnTitle}>
                    {findingsContents.review.reviewCaptions[index]}
                  </Typography>
                )}
                <img className={classes.image} src={`${BASE_URL}${image.path}`} alt={image.alt} />
              </div>
            ))}
          </div>
        </div>
        <Typography
          onClick={togglePanel}
          className={trainingId === 1 ? classes.axuminFindingsButton : classes.poslumaFindingsButton}
        >
          {expanded ? '-  ' : '+  '}
          <span className={classes.buttonText}>FINDINGS</span>
        </Typography>
        {expanded && (
          <div>
            {findingsContents.findings.reviewText && (
              <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.text}>
                {findingsContents.findings.reviewText}
              </ReactMarkdown>
            )}
            <div className={findingsContents.layoutInColumns ? classes.columnWrapper : classes.rowWrapper}>
              {findingsContents.findings.findingsImages.map((image, index) => (
                <div className={findingsContents.layoutInColumns ? classes.column : classes.row} key={image.path}>
                  {findingsContents.findings.findingsCaptions && (
                    <Typography className={classes.columnTitle}>
                      {findingsContents.findings.findingsCaptions[index]}
                    </Typography>
                  )}
                  <img className={classes.image} src={`${BASE_URL}${image.path}`} alt={image.alt} />
                </div>
              ))}
            </div>
            <Typography className={trainingId === 1 ? classes.axuminFindingsTitle : classes.poslumaFindingsTitle}>
              - FINDINGS
            </Typography>
            <ul className={trainingId === 1 ? classes.axuminFindingsList : classes.poslumaFindingsList}>
              {findingsContents.findings.listElements.map((text) => (
                <li key={text}>{text}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </MuiThemeProvider>
  );
};

export default FindingsPanel;
